import { useLocation } from 'react-router-dom'
import type { ModuleId, ModuleTheme } from '@/constants/modules'
import { getModuleTheme } from '@/constants/modules'
import { resolveModuleFromPath } from '@/utils/module-theme'
import { cn } from '@/utils/cn'

/** Full class strings so Tailwind can purge-detect them */
const TAB_ACTIVE: Record<ModuleId, string> = {
  dashboard:
    'data-[state=active]:bg-blue-500 data-[state=active]:text-white data-[state=active]:shadow-sm',
  'master-data':
    'data-[state=active]:bg-teal-600 data-[state=active]:text-white data-[state=active]:shadow-sm',
  sales:
    'data-[state=active]:bg-emerald-500 data-[state=active]:text-white data-[state=active]:shadow-sm',
  inventory:
    'data-[state=active]:bg-amber-500 data-[state=active]:text-white data-[state=active]:shadow-sm',
  production:
    'data-[state=active]:bg-orange-500 data-[state=active]:text-white data-[state=active]:shadow-sm',
  dispatch:
    'data-[state=active]:bg-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-sm',
  purchases:
    'data-[state=active]:bg-violet-500 data-[state=active]:text-white data-[state=active]:shadow-sm',
  tasks:
    'data-[state=active]:bg-sky-600 data-[state=active]:text-white data-[state=active]:shadow-sm',
  finance:
    'data-[state=active]:bg-rose-500 data-[state=active]:text-white data-[state=active]:shadow-sm',
  reports:
    'data-[state=active]:bg-indigo-500 data-[state=active]:text-white data-[state=active]:shadow-sm',
  'user-management':
    'data-[state=active]:bg-teal-500 data-[state=active]:text-white data-[state=active]:shadow-sm',
  settings:
    'data-[state=active]:bg-zinc-600 data-[state=active]:text-white data-[state=active]:shadow-sm',
}

/**
 * Resolves the active module theme from an optional override or the current route.
 * Use in pages, tables, dialogs, and empties so future modules inherit styling automatically.
 */
export function useModuleTheme(moduleId?: ModuleId): ModuleTheme & {
  moduleId: ModuleId
  /** Primary CTA button classes (create/save/confirm in body/modals) */
  actionBtn: string
  /** Primary CTA on the colored PageLayout banner */
  headerActionBtn: string
  /** Soft modal/header strip */
  modalHeader: string
  /** Soft footer strip */
  modalFooter: string
  /** Selected chip / pill */
  chipActive: string
  /** Unselected chip / pill */
  chipIdle: string
  /** Selected list row (sidebar-style pickers) */
  listActive: string
  /** Active tab trigger */
  tabActive: string
} {
  const location = useLocation()
  const id = moduleId ?? resolveModuleFromPath(location.pathname)
  const theme = getModuleTheme(id)

  return {
    ...theme,
    moduleId: id,
    // Primary CTAs — brand maroon → navy (`.btn-cta`); module accent stays for chips/tabs.
    actionBtn: cn(
      'btn-cta text-primary-foreground',
      'focus-visible:ring-2 focus-visible:ring-ring/25 focus-visible:ring-offset-1',
    ),
    headerActionBtn:
      'btn-cta h-8 rounded-lg px-2.5 text-[11px] font-semibold text-primary-foreground sm:h-9 sm:px-3 sm:text-sm',
    modalHeader: 'border-border bg-card',
    modalFooter: 'border-border bg-card',
    chipActive: cn(
      theme.accent,
      'border-transparent text-white shadow-sm ring-1 ring-black/5',
    ),
    chipIdle:
      'border-border/80 bg-card text-muted-foreground hover:border-border hover:bg-secondary hover:text-foreground',
    listActive: cn(theme.accent, 'text-white shadow-sm'),
    tabActive: TAB_ACTIVE[id],
  }
}
